import type { UsageMetric } from '../services/api'
import { Button, Card, Hint, Inline, Meter, Stack } from '../ds'
import UsageMeter from '../components/UsageMeter'
import type { MonthUsage } from './useMonthUsage'

/**
 * The profile menu's "This month" section (MAR-413; design: canvas page "Account menu · usage").
 *
 * Under plan every allowance is Billing's own meter (`UsageMeter`). Over plan, a metered one
 * (conversations, emails) becomes a card that says what it has cost so far and how far is
 * left before that channel pauses — the overage cap is real: `mau-calculator` shadows the
 * channel nightly once usage reaches the plan + its overage allowance (pricing PRD §3.3/§3.4),
 * and the menu states it rather than letting a merchant find out from a quiet widget.
 *
 * The upgrade CTA appears only when something is over plan AND a self-serve tier above this
 * one exists; it goes to Billing, where `PricingView` does the change.
 */
export interface MonthUsageSectionProps {
  usage: MonthUsage
  onOpenBilling: () => void
}

/** "$79" for a whole amount, "$4.60" otherwise. */
const money = (cents: number) => `$${cents % 100 === 0 ? cents / 100 : (cents / 100).toFixed(2)}`
const count = (n: number) => n.toLocaleString()
// ⚠️ In UTC, not the browser's zone: the reset is 00:00 UTC on the 1st, which west of UTC is
// still the 31st locally — "Resets 31 Oct" for a rollover that happens on 1 Nov.
const day = (iso: string | null | undefined) => {
  const d = iso ? new Date(iso) : null
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
    : null
}

/** Over plan on a metered allowance: there is an overage block, and usage is past the plan. */
const isOverPlan = (m: UsageMetric | null): m is UsageMetric & { overage: NonNullable<UsageMetric['overage']> } =>
  !!m && !!m.overage && m.overage.allowance > 0 && m.used > m.limit

interface OverPlanMeterProps {
  label: string
  metric: UsageMetric & { overage: NonNullable<UsageMetric['overage']> }
  /** What pauses, as a sentence subject: "chat" / "email". */
  channel: string
  resetsOn: string | null
  /** False when the pause cannot be stated from these numbers (see `workspaceCount`). */
  showPause: boolean
  /** A trial is not billed: no charge is shown. */
  trial: boolean
  sage?: boolean
}

export function OverPlanMeter({ label, metric, channel, resetsOn, showPause, trial, sage }: OverPlanMeterProps) {
  const { used, limit, overage } = metric
  const cap = limit + overage.allowance
  const over = used - limit
  const left = Math.max(0, cap - used)
  const until = resetsOn ? `until ${resetsOn}` : 'until the month resets'
  const Channel = channel.charAt(0).toUpperCase() + channel.slice(1)
  return (
    <Card tone={sage ? 'sage' : 'gold'} padding="sm">
      <Stack gap="sm">
        <Meter
          label={label}
          value={Math.min(used, cap)}
          max={cap}
          included={limit}
          valueText={`${count(used)} / ${count(limit)}`}
          tone={sage ? 'sage' : 'gold'}
        />
        <Inline justify="between">
          <Hint as="span">Plan {count(limit)}</Hint>
          {showPause && <Hint as="span">Pauses at {count(cap)}</Hint>}
        </Inline>
        <p className="t-small">
          <strong>
            {count(over)} over your plan
            {trial ? '' : ` · ${money(overage.chargeCents)}${left > 0 ? ' so far' : ''}`}.
          </strong>
          {/* The pause is applied by the nightly enforcement run, so at the cap it is imminent,
              not necessarily in force yet — the copy says "pauses", never "is paused". */}
          {showPause &&
            (left > 0
              ? ` ${count(left)} more and ${channel} pauses ${until}.`
              : ` ${Channel} has reached its cap and pauses ${until}.`)}
        </p>
      </Stack>
    </Card>
  )
}

export default function MonthUsageSection({ usage, onOpenBilling }: MonthUsageSectionProps) {
  const resetsOn = day(usage.resetsAt)
  // The pause line is a claim about what `enforceWorkspace` will do. It is true only when:
  // - the account has exactly one workspace AND enforcement acts on it (`pausable`, from the
  //   server's counts) — usage here is the ACCOUNT's total, the pause is decided per WORKSPACE,
  //   and synthetic/shadow/disabled workspaces are never enforced;
  // - the trial has not ended — an expired trial's workspace is shadowed by trial-manager, which
  //   can lag the API's own "expired" by a night, so it is not left to the count.
  // A cancellation is NOT excluded: nothing shadows on cancel, and the cap still applies.
  const showPause = usage.pausable && usage.trial !== 'ended'
  const trialEnds = day(usage.trialEndsAt)
  const shared = { resetsOn, showPause, trial: usage.trial !== null }
  const overAny = isOverPlan(usage.conversations) || isOverPlan(usage.emails)
  const bill = usage.bill
  return (
    <Stack gap="sm">
      <Inline justify="between">
        <span className="t-eyebrow">
          This month{usage.tierName ? ` · ${usage.tierName}${usage.trial ? ' trial' : ''}` : ''}
        </span>
        {usage.cancelled ? (
          <Hint as="span">Cancelled</Hint>
        ) : usage.trial === 'ended' ? (
          <Hint as="span">Trial ended</Hint>
        ) : usage.trial === 'trial' && trialEnds ? (
          <Hint as="span">Trial ends {trialEnds}</Hint>
        ) : null}
      </Inline>

      {usage.conversations &&
        (isOverPlan(usage.conversations) ? (
          <OverPlanMeter label="Conversations" metric={usage.conversations} channel="chat" {...shared} />
        ) : (
          <UsageMeter label="Conversations" metric={usage.conversations} />
        ))}
      <UsageMeter label="Active customers" metric={usage.mau} />
      {usage.emails &&
        (isOverPlan(usage.emails) ? (
          <OverPlanMeter label="Emails" metric={usage.emails} channel="email" {...shared} sage />
        ) : (
          <UsageMeter label="Emails" metric={usage.emails} sage />
        ))}
      {/* Said under the meters, not in the header: active customers is a rolling 30 days and
          never "resets" — only the conversation and email allowances do, on the 1st. */}
      {(usage.conversations || usage.emails) && (
        <Hint as="span">
          {usage.conversations && usage.emails ? 'Conversations and emails reset' : usage.conversations ? 'Conversations reset' : 'Emails reset'} {resetsOn}.
        </Hint>
      )}

      {bill && (
        <Card tone="recessed" padding="sm">
          <Inline justify="between" wrap={false}>
            <span>
              <Hint as="span">Bill so far</Hint>
              <br />
              <span className="t-card-title">{money(bill.totalCents)}</span>{' '}
              <Hint as="span">
                {money(bill.baseCents)} plan{bill.overageCents > 0 ? ` + ${money(bill.overageCents)} over` : ''}
              </Hint>
            </span>
            {overAny && usage.nextTier && (
              <Button size="sm" onClick={onOpenBilling}>
                Move to {usage.nextTier}
              </Button>
            )}
          </Inline>
        </Card>
      )}
      <Inline justify="end">
        <Button variant="link" aria-label="Usage and billing" onClick={onOpenBilling}>
          Usage and billing →
        </Button>
      </Inline>
    </Stack>
  )
}
